import { Box, Stack, Typography } from '@mui/material';
import { CharacterGrade, SoundGrade } from '../../mockLessonContent';
import { ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SURFACE_SOFT, YELLOW } from '../../studentStyles';

const gradeColors: Record<SoundGrade, string> = { good: GREEN, ok: YELLOW, bad: RED };

const legend = [
    { color: GREEN, label: 'Good' },
    { color: YELLOW, label: 'Close' },
    { color: RED, label: 'Needs practice' },
];

// Per-character initial/final/tone grades — the student-side echo of the
// teacher Speaking review grading (LessonSpeakingReviewPage).
export default function PronunciationDetail({ grades }: { grades: CharacterGrade[] }) {
    const needsWork = grades.some(g => g.initial !== 'good' || g.final !== 'good' || g.tone !== 'good');

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {grades.map((grade, index) => {
                    const alignment = grade.alignment ?? 'speech';
                    return (
                        <Box
                            key={`${grade.character}-${index}`}
                            sx={{
                                minWidth: 56, p: 1, borderRadius: '12px', backgroundColor: SURFACE_SOFT, textAlign: 'center',
                                border: alignment === 'speech' ? 'none' : `2px dashed ${alignment === 'silence' ? RED : SLATE}`,
                            }}
                        >
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.2 }}>
                                {grade.character}
                            </Typography>
                            {/* Target pinyin, then what we heard directly under it. */}
                            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.25 }}>
                                {grade.pinyinExpected}
                            </Typography>
                            <Typography sx={{
                                fontFamily: INTER,
                                fontSize: 11,
                                fontWeight: grade.pinyinSpoken === grade.pinyinExpected ? 400 : 700,
                                color: grade.pinyinSpoken === grade.pinyinExpected ? SLATE : ERROR_TEXT,
                            }}>
                                {grade.pinyinSpoken}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '3px', mt: 0.75 }}>
                                {([['initial', grade.initial], ['final', grade.final], ['tone', grade.tone]] as const).map(([part, value]) => (
                                    <Box key={part} sx={{ flex: 1, height: 6, borderRadius: '999px', backgroundColor: gradeColors[value] }} />
                                ))}
                            </Box>
                            {alignment !== 'speech' && (
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: alignment === 'silence' ? ERROR_TEXT : SLATE, mt: 0.5 }}>
                                    {alignment === 'silence' ? 'not heard' : 'extra sound'}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                    Pinyin: target · heard
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                    Bars: Initial · Final · Tone
                </Typography>
                {legend.map(item => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                        <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>{item.label}</Typography>
                    </Box>
                ))}
            </Stack>
            {needsWork && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center', mt: 1.5 }}>
                    Tip: play the model again and copy the tone shape — slow is fine.
                </Typography>
            )}
        </Box>
    );
}
